// ─────────────────────────────────────────────
//  Cascade AI — Anthropic Provider
// ─────────────────────────────────────────────

import Anthropic from '@anthropic-ai/sdk';
import type {
  ConversationMessage,
  GenerateOptions,
  GenerateResult,
  ImageAttachment,
  ModelInfo,
  ProviderConfig,
  StreamChunk,
} from '../types.js';
import { MODELS } from '../constants.js';
import { BaseProvider } from './base.js';

// Anthropic extended thinking — only the 4.x reasoning models (Opus 4 / Sonnet 4)
// support it. budget_tokens must be >= 1024 and < max_tokens; we cap well under
// the cap. Returns {} for unsupported models so their requests are unchanged.
function anthropicThinkingParam(modelId: string, maxTokens: number): { thinking?: { type: 'enabled'; budget_tokens: number } } {
  if (!/claude-(opus|sonnet)-4/i.test(modelId)) return {};
  const budget = Math.min(8000, maxTokens - 1024);
  if (budget < 1024) return {};
  return { thinking: { type: 'enabled', budget_tokens: budget } };
}

export class AnthropicProvider extends BaseProvider {
  private client: Anthropic;

  constructor(config: ProviderConfig, model: ModelInfo) {
    super(config, model);
    // OAuth bearer (e.g. a Claude Code subscription token) authenticates via
    // Authorization: Bearer + the oauth beta header instead of x-api-key.
    if (config.authToken) {
      this.client = new Anthropic({
        authToken: config.authToken,
        defaultHeaders: { 'anthropic-beta': 'oauth-2025-04-20' },
      });
    } else {
      this.client = new Anthropic({
        apiKey: config.apiKey,
      });
    }
  }

  async generate(options: GenerateOptions): Promise<GenerateResult> {
    const chunks: StreamChunk[] = [];
    return this.generateStream(options, (c) => chunks.push(c));
  }

  async generateStream(
    options: GenerateOptions,
    onChunk: (chunk: StreamChunk) => void,
  ): Promise<GenerateResult> {
    const messages = this.convertMessages(options.messages);
    const tools = options.tools?.map((t) => ({
      name: t.name,
      description: t.description,
      input_schema: t.inputSchema as Anthropic.Tool['input_schema'],
    }));

    let fullContent = '';
    let inputTokens = 0;
    let outputTokens = 0;

    const maxTokens = options.maxTokens ?? this.model.maxOutputTokens;
    const thinkParam = anthropicThinkingParam(this.model.id, maxTokens);
    const useThinking = !!thinkParam.thinking;
    const stream = this.client.messages.stream({
      model: this.model.id,
      max_tokens: maxTokens,
      // Extended thinking requires temperature = 1; otherwise honor the request.
      temperature: useThinking ? 1 : (options.temperature ?? 0.7),
      system: options.systemPrompt,
      messages,
      tools: tools?.length ? tools : undefined,
      ...thinkParam,
    }, { signal: options.signal });

    let isThinking = false;

    for await (const event of stream) {
      if (event.type === 'content_block_delta') {
        if ((event.delta as any).type === 'thinking_delta') {
          if (!isThinking) {
            isThinking = true;
            fullContent += '<think>\n';
            onChunk({ text: '<think>\n', finishReason: null });
          }
          const text = (event.delta as any).thinking;
          fullContent += text;
          onChunk({ text, finishReason: null });
        } else if (event.delta.type === 'text_delta') {
          if (isThinking) {
            isThinking = false;
            fullContent += '\n</think>\n\n';
            onChunk({ text: '\n</think>\n\n', finishReason: null });
          }
          const text = event.delta.text;
          fullContent += text;
          onChunk({ text, finishReason: null });
        }
      } else if (event.type === 'message_delta' && event.usage) {
        outputTokens = event.usage.output_tokens;
      } else if (event.type === 'message_start' && event.message.usage) {
        inputTokens = event.message.usage.input_tokens;
      }
    }

    if (isThinking) {
      fullContent += '\n</think>\n\n';
      onChunk({ text: '\n</think>\n\n', finishReason: null });
    }

    const finalMessage = await stream.finalMessage();
    const toolCalls = finalMessage.content
      .filter((b): b is Anthropic.ToolUseBlock => b.type === 'tool_use')
      .map((b) => ({
        id: b.id,
        name: b.name,
        input: b.input as Record<string, unknown>,
      }));

    onChunk({ text: '', finishReason: finalMessage.stop_reason as GenerateResult['finishReason'] });

    return {
      content: fullContent,
      usage: this.makeUsage(inputTokens, outputTokens),
      toolCalls: toolCalls.length ? toolCalls : undefined,
      finishReason: (finalMessage.stop_reason as GenerateResult['finishReason']) ?? 'stop',
    };
  }

  async countTokens(text: string): Promise<number> {
    // Anthropic token counting is often simplified to 4 chars per token if the SDK doesn't support it directly
    return Math.ceil(text.length / 4);
  }

  async listModels(): Promise<ModelInfo[]> {
    try {
      const resp = await fetch('https://api.anthropic.com/v1/models', {
        headers: {
          'x-api-key': this.config.apiKey ?? '',
          'anthropic-version': '2023-06-01',
        },
      });
      // Anthropic returns JSON-encoded error objects ({ type: "error", ... })
      // for 4xx/5xx responses. Calling `.data.map` on that crashes the caller
      // and hides the real authentication / network error. Fall through to
      // the hardcoded model list instead.
      if (!resp.ok) {
        return Object.values(MODELS).filter((m) => m.provider === 'anthropic');
      }
      const data = await resp.json() as { data?: Array<{ id: string; display_name: string }> };
      if (!Array.isArray(data?.data)) {
        return Object.values(MODELS).filter((m) => m.provider === 'anthropic');
      }

      return data.data.map((m) => {
        const known = Object.values(MODELS).find((km) => km.id === m.id && km.provider === 'anthropic');
        if (known) return known;

        return {
          id: m.id,
          name: m.display_name || m.id,
          provider: 'anthropic' as const,
          contextWindow: m.id.includes('3.5-sonnet') ? 200_000 : 100_000,
          isVisionCapable: true,
          inputCostPer1kTokens: 0,
          outputCostPer1kTokens: 0,
          maxOutputTokens: 8_000,
          supportsStreaming: true,
          isLocal: false,
        };
      });
    } catch {
      return Object.values(MODELS).filter((m) => m.provider === 'anthropic');
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Available with either an API key or an OAuth bearer token.
      return !!(this.config.apiKey || this.config.authToken);
    } catch {
      return false;
    }
  }

  private convertMessages(messages: ConversationMessage[]): Anthropic.MessageParam[] {
    const result: Anthropic.MessageParam[] = [];

    for (const m of messages) {
      // System messages in history are skipped — they're passed via the top-level `system` field
      if (m.role === 'system') continue;

      // ── Tool result messages → Anthropic tool_result content block ─────
      if (m.role === 'tool') {
        const toolContent = typeof m.content === 'string' ? m.content : JSON.stringify(m.content);
        result.push({
          role: 'user',
          content: [{
            type: 'tool_result',
            tool_use_id: m.toolCallId ?? '',
            content: toolContent,
          }] as any,
        });
        continue;
      }

      // ── Assistant messages: may carry tool_use blocks ──────────────────
      if (m.role === 'assistant') {
        const content: any[] = [];

        // Text part
        const text = typeof m.content === 'string' ? m.content : '';
        if (text) content.push({ type: 'text', text });

        // Tool calls → tool_use blocks
        for (const tc of m.toolCalls ?? []) {
          content.push({
            type: 'tool_use',
            id: tc.id,
            name: tc.name,
            input: tc.input,
          });
        }

        if (content.length > 0) {
          result.push({ role: 'assistant', content });
        }
        continue;
      }

      // ── User messages ──────────────────────────────────────────────────
      if (m.role === 'user') {
        if (typeof m.content === 'string') {
          result.push({ role: 'user', content: m.content });
        } else {
          const content: any[] = m.content.map((block) => {
            if (block.type === 'text') return { type: 'text' as const, text: block.text };
            if (block.type === 'image') {
              const img = block.image as ImageAttachment;
              if (img.type === 'base64') {
                return {
                  type: 'image' as const,
                  source: { type: 'base64' as const, media_type: img.mimeType, data: img.data },
                };
              }
              return { type: 'image' as const, source: { type: 'url' as const, url: img.data } as any };
            }
            return { type: 'text' as const, text: '' };
          });
          result.push({ role: 'user', content });
        }
      }
    }

    return result;
  }
}
